import { useMemo } from 'react'

import type { LotOption } from './lot-items-field'
import type { LotPickerSource } from './lot-picker-source'
import { Combobox } from '@/components/ui/combobox'
import { m } from '@/lib/paraglide/messages'

interface Props {
    /** Nguồn từ `useXLotSource` — quyết định loại lô, bộ lọc và cách hiển thị. */
    source: LotPickerSource
    value: string
    /** Trả kèm option để caller đọc số dư/đơn vị mà không phải tra lại. */
    onChange: (lotId: string, option?: LotOption) => void
    /** Lô đã chọn ở dòng khác — ẩn khỏi danh sách (trừ chính lô của dòng này)
     *  để không chọn trùng một lô hai lần. */
    excludeIds?: string[]
    placeholder?: string
    searchPlaceholder?: string
    emptyText?: string
    disabled?: boolean
    id?: string
    className?: string
    /** Options cố định đã đủ (vd lô trong phiếu lấy hàng): lọc trên máy, không
     *  gọi server, không reset từ khoá khi đóng. */
    clientFilter?: boolean
}

/**
 * Ô chọn MỘT lô, dùng chung cho mọi loại lô. Tìm phía server có debounce và
 * cuộn tải thêm đều do `source` lo; component này chỉ nối chúng vào Combobox.
 */
export function LotPicker({
    source,
    value,
    onChange,
    excludeIds,
    placeholder,
    searchPlaceholder,
    emptyText,
    disabled,
    id,
    className,
    clientFilter = false,
}: Props) {
    const { options: all, getOption, onSearchChange } = source
    const options = useMemo(() => {
        if (!excludeIds?.length) return all
        const exclude = new Set(excludeIds)
        return all.filter((o) => o.value === value || !exclude.has(o.value))
    }, [all, excludeIds, value])

    return (
        <Combobox
            id={id}
            className={className}
            options={options}
            value={value}
            selectedOption={getOption(value)}
            onChange={(v) => onChange(v, getOption(v))}
            onSearchChange={clientFilter ? undefined : onSearchChange}
            shouldFilter={clientFilter}
            onLoadMore={clientFilter ? undefined : source.onLoadMore}
            hasMore={source.hasMore}
            loadingMore={source.loadingMore}
            // Ô tìm nằm trong popover bị unmount khi đóng: không xoá từ khoá thì
            // mở lại thấy ô rỗng mà danh sách vẫn lọc theo từ cũ.
            onOpenChange={
                clientFilter
                    ? undefined
                    : (open) => {
                          if (!open) onSearchChange('')
                      }
            }
            disabled={disabled}
            placeholder={placeholder ?? m.lot_items_lot_picker_placeholder()}
            searchPlaceholder={
                searchPlaceholder ?? m.lot_items_lot_picker_search()
            }
            emptyText={emptyText}
        />
    )
}
